import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

export type ActivityEventType =
  | 'SENT'
  | 'DELIVERED'
  | 'OPENED'
  | 'CLICKED'
  | 'REPLIED'
  | 'BOUNCED'
  | 'UNSUBSCRIBED'
  | 'SPAM_REPORTED';

export interface MetricCounts {
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  unsubscribed: number;
}

export interface MetricRates {
  open_rate: number;
  click_rate: number;
  reply_rate: number;
  bounce_rate: number;
}

export type ReportSummary = MetricCounts & MetricRates;

export interface DailyEngagementPoint {
  date: string;
  sent: number;
  opened: number;
  replied: number;
}

export interface CampaignReportRow {
  id: string;
  name: string;
  type: string;
  status: string;
  metrics: MetricCounts;
  rates: MetricRates;
}

export interface ActivityFeedItem {
  id: string;
  event_type: ActivityEventType;
  occurred_at: string;
  campaign_id: string | null;
  campaign_name: string | null;
  enrollment_id: string | null;
  contact_email: string | null;
  metadata: Record<string, unknown> | null;
}

export interface ReportsFilters {
  date_from?: string;
  date_to?: string;
  campaign_id?: string;
}

export function useReportsSummary(filters: ReportsFilters = {}) {
  return useQuery<ReportSummary>({
    queryKey: ['reports', 'summary', filters],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ReportSummary }>('/reports/summary', { params: filters });
      return data.data;
    },
  });
}

export function useEngagement(opts: ReportsFilters & { days?: number } = {}) {
  return useQuery<DailyEngagementPoint[]>({
    queryKey: ['reports', 'engagement', opts],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: DailyEngagementPoint[] }>('/reports/engagement', { params: opts });
      return data.data;
    },
  });
}

export function useCampaignReports(opts: { date_from?: string; date_to?: string; limit?: number; sort?: 'reply_rate' | 'sent' } = {}) {
  return useQuery<CampaignReportRow[]>({
    queryKey: ['reports', 'campaigns', opts],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: CampaignReportRow[] }>('/reports/campaigns', { params: opts });
      return data.data;
    },
  });
}

export function useActivityFeed(opts: { campaign_id?: string; limit?: number } = {}) {
  return useQuery<ActivityFeedItem[]>({
    queryKey: ['reports', 'activity', opts],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: ActivityFeedItem[] }>('/reports/activity', { params: opts });
      return data.data;
    },
    // Refresh activity once a minute so a recruiter watching a campaign sees
    // new sends/opens land without a hard refresh.
    refetchInterval: 60_000,
  });
}

/**
 * Merged campaign timeline — recipient events (sent/opened/etc) interleaved
 * with actor audit entries (launch / pause / settings change) for the
 * Campaign Detail Activity tab.
 */
export interface MergedActivityItem {
  id: string;
  source: 'activity' | 'audit';
  kind: string;
  occurred_at: string;
  actor_user_id: string | null;
  actor_email: string | null;
  contact_email: string | null;
  metadata: Record<string, unknown> | null;
}

export function useCampaignTimeline(campaignId: string, opts: { limit?: number } = {}) {
  return useQuery<MergedActivityItem[]>({
    queryKey: ['reports', 'campaign-timeline', campaignId, opts],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: MergedActivityItem[] }>(
        `/reports/campaigns/${campaignId}/timeline`,
        { params: opts }
      );
      return data.data;
    },
    enabled: !!campaignId,
    refetchInterval: 60_000,
  });
}
